import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FeeService } from '../service/fee.service';
import {
  FeeGroupResponse,
  MemberSubgroupInfo,
  RegisterToSubgroupRequest,
  SubgroupOption
} from '../interface/fee.interface';

export interface RegisterSubgroupDialogData {
  memberId: number;
  industryId: number;
  currentSubgroups: MemberSubgroupInfo[];
}

@Component({
  selector: 'app-fee-register-subgroup-dialog',
  templateUrl: './fee-register-subgroup-dialog.component.html',
  styleUrls: ['./fee-register-subgroup-dialog.component.scss']
})
export class FeeRegisterSubgroupDialogComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  options: SubgroupOption[] = [];
  loading = true;
  saving = false;

  constructor(
    private readonly dialogRef: MatDialogRef<FeeRegisterSubgroupDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: RegisterSubgroupDialogData,
    private readonly feeService: FeeService
  ) {}

  ngOnInit(): void {
    this.feeService.getFeeGroups(this.data.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (groups: FeeGroupResponse[]) => {
          const currentIds = new Set(this.data.currentSubgroups.map(s => s.id));
          this.options = groups.flatMap(g =>
            g.subgroups.map(sg => ({
              subgroupId: sg.id,
              subgroupName: sg.name,
              groupId: g.id,
              groupName: g.name,
              selected: false,
            }))
          ).filter(option => !currentIds.has(option.subgroupId));
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedLabel(): string {
    const selected = this.options.find(o => o.selected);
    if (!selected) return '';
    return `${selected.groupName} (${selected.subgroupName})`;
  }

  get canSave(): boolean {
    return this.options.some(o => o.selected);
  }

  toggleOption(index: number, checked: boolean): void {
    this.options = this.options.map((o, i) => ({
      ...o,
      selected: checked && i === index
    }));
  }

  save(): void {
    if (this.saving) return;
    const selectedOption = this.options.find(o => o.selected);

    if (!selectedOption) {
      this.dialogRef.close();
      return;
    }

    const request: RegisterToSubgroupRequest = { subgroupIds: [selectedOption.subgroupId] };
    this.saving = true;
    this.feeService.registerMemberToSubgroups(this.data.memberId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updated => this.dialogRef.close(updated),
        error: () => { this.saving = false; }
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
